/**
 * Whether the signed-in user is LATO staff (`GET /admin/me`). The `/admin` layout's route guard
 * runs this before any admin page loads, so it lives apart from the admin data hooks in
 * `useAdmin.ts`: whatever the guard imports ships with every page.
 */

import { apiRequest } from "@lato/client/lib/apiClient";

export interface AdminMe {
  user_id: string;
  email: string;
  is_platform_admin: boolean;
}

// 403 from GET /admin/me means "authenticated, but not a platform admin" — the
// route guard turns this into a sign-out + redirect rather than a /signin loop.
export class NotPlatformAdminError extends Error {
  constructor() {
    super("Not a platform admin");
    this.name = "NotPlatformAdminError";
  }
}

export const adminMeQueryOptions = {
  queryKey: ["admin", "me"] as const,
  queryFn: async (): Promise<AdminMe> => {
    const res = await apiRequest("/admin/me", { method: "GET" });
    if (res.status === 403) throw new NotPlatformAdminError();
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return (await res.json()) as AdminMe;
  },
  retry: false,
  staleTime: 1000 * 60 * 5,
};
